import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "../lib/utils";
import logo from "../asset/home/CPA.png";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const links = [
    { name: "Home", path: "/" },
    { name: "About Firm", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Expertise", path: "/expertise" },
    { name: "Leadership", path: "/team" },
  ];

  const isActive = (path) =>
    location.pathname === path ||
    (path === "/services" && location.pathname.startsWith("/services/"));

  return (
    <header className="fixed w-full top-0 z-50 flex flex-col">
      {/* Top Bar - Contact Info */}
      <div className="bg-[#203468] text-slate-300 py-2 hidden md:block text-xs font-medium tracking-wider uppercase">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex gap-6">
            <span className="flex items-center gap-2">
              <Phone className="h-3 w-3" /> +1 425 221 6660
            </span>
            <span className="flex items-center gap-2">
              <Mail className="h-3 w-3" /> connectus@thealphacpas.com
            </span>
          </div>
          <div>Global Chartered Accountants & Advisors</div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav
        className={cn(
          "transition-all duration-300 border-b",
          scrolled
            ? "bg-white/95 backdrop-blur-md border-slate-200 py-3 shadow-sm"
            : "bg-white border-transparent py-5",
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src={logo}
                alt="The Alpha CPAs logo"
                className="h-10 w-auto object-contain"
              />
              <div className="flex flex-col">
                <div className="flex flex-col">
                  <span className="font-serif text-2xl font-bold text-slate-900 tracking-tight leading-none mb-1">
                    Alpha <span className="text-orange-500">Accountants</span>
                  </span>
                  <span className="text-slate-500 text-xss font-medium tracking-wide">
                    Unleashing growth through Synergies
                  </span>
                </div>
              </div>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              {links.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={cn(
                    "text-sm font-semibold uppercase tracking-wider transition-colors hover:text-[#019BD7] relative py-2",
                    isActive(link.path)
                      ? "text-[#019BD7]"
                      : "text-slate-600",
                  )}
                >
                  {link.name}
                  {isActive(link.path) && (
                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[#019BD7] rounded-full"></span>
                  )}
                </Link>
              ))}
              <Link
                to="/contact"
                className="bg-[#019BD7] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-[#018ac0] transition-all duration-300 shadow-lg shadow-[#019BD7]/20 hover:shadow-[#019BD7]/40"
              >
                Contact Us
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center md:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-slate-900 hover:text-[#019BD7] focus:outline-none p-2"
              >
                {isOpen ? (
                  <X className="h-7 w-7" />
                ) : (
                  <Menu className="h-7 w-7" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 absolute w-full shadow-xl">
            <div className="px-4 pt-2 pb-6 space-y-1">
              {links.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    "block px-4 py-4 text-sm font-semibold uppercase tracking-wider border-b border-slate-50",
                    isActive(link.path)
                      ? "text-[#019BD7] bg-slate-50"
                      : "text-slate-600",
                  )}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setIsOpen(false)}
                className="block px-4 py-4 text-sm font-semibold uppercase tracking-wider text-[#019BD7]"
              >
                Client Portal & Contact
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
